<script setup lang="ts">
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import SimpleTable from '@/components/tables/SimpleTable.vue';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { entraAuthService } from '@/services/auth/entra-auth-service';
import { calculationBudgetService } from '@/services/calculation-budget/calculation-budget-service';
import { notificationService } from '@/services/notification/notification-service';
import { fetchAccounts } from '@/utils/accounts/fetch-accounts';
import type { BaseColumnData } from '@/utils/tables/column-data';
import {
  PhArrowDown,
  PhArrowUp,
  PhFileArrowDown,
  PhFloppyDisk,
  PhPlus,
  PhTrash,
} from '@phosphor-icons/vue';
import {
  calculateGrossCost,
  calculateNetCost,
  models,
  type Nullish,
  pagePermissions,
  units,
  utils,
} from 'cesg-shared';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import formattedNumberInput from '../common/formatted-number-input.vue';

interface formDataInterface {
  id: Nullish<string>;
  unit: Nullish<string>;
  netCost: Nullish<number>;
  budget: Nullish<number>;
  comment: Nullish<string>;
  markupFactor: Nullish<number>;
  numberOfItems: Nullish<number>;
  accountNumber: Nullish<number>;
  pricePerUnit: Nullish<number>;
  grossCost: Nullish<number>;
  rowIndex: number;
}

overlaySpinnerService.show();
const { t } = useI18n();
const formData = ref<formDataInterface[]>([]);
const originalFormData = ref<formDataInterface[]>([]);
const loading = ref<boolean>(false);
const accounts = ref<DropDownItem[]>();
const errors = ref<Record<string, boolean>>({});
const route = useRoute();
const projectId = computed(() => utils.type.asType<string>(route.params.id));
const headerInputs = reactive<Record<string, string>>({});
const user = entraAuthService.me.value;
const columns: BaseColumnData[] = [
  {
    key: 'accountNumber',
    label: t('calculationBudget.table.accountNumber'),
    editable: () => true,
    sortable: true,
    columnWidth: '17%',
  },
  {
    key: 'numberOfItems',
    label: t('calculationBudget.table.numberOfItems'),
    editable: () => true,
    editableInputType: 'number',
    sortable: true,
    columnWidth: '9%',
  },
  {
    key: 'unit',
    label: t('calculationBudget.table.unit'),
    editable: () => true,
    sortable: true,
    columnWidth: '9%',
  },
  {
    key: 'pricePerUnit',
    label: t('calculationBudget.table.pricePerUnit'),
    editable: () => true,
    editableInputType: 'number',
    format: utils.formatNumber,
    sortable: true,
    columnWidth: '14%',
  },
  {
    key: 'markupFactor',
    label: t('ata.details.costs.markupFactor'),
    editable: () => true,
    format: utils.formatNumber,
    editableInputType: 'number',
    prefix: '%',
    sortable: true,
    columnWidth: '8%',
  },
  {
    key: 'grossCost',
    label: t('calculationBudget.table.grossCost'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
    columnWidth: '10%',
  },
  {
    key: 'netCost',
    label: t('calculationBudget.table.netCost'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
    columnWidth: '11%',
  },
  {
    key: 'budget',
    label: t('calculationBudget.table.budget'),
    editable: () => true,
    editableInputType: 'number',
    sortable: true,
    columnWidth: '9%',
  },
  {
    key: 'comment',
    label: t('calculationBudget.table.comment'),
    editable: () => true,
    editableInputType: 'string',
    sortable: true,
    columnWidth: '10%',
  },
  { key: 'actions', columnWidth: '13%' },
];

function handleHeaderInput(payload: {
  columnKey: string;
  value: string;
}): void {
  headerInputs[payload.columnKey] = payload.value;
}

const saveCalculationBudgets = async () => {
  if (!validateFields()) {
    return;
  }
  loading.value = true;

  const projectId = utils.type.asType<string>(route.params.id);

  const body: models.UpCreateCalculationBudgetBodyRow[] = formData.value.map(
    (item) => ({
      id: item.id ?? null,
      accountNumber: item.accountNumber ?? 0,
      numberOfItems: item.numberOfItems ?? null,
      unit: item.unit ?? null,
      pricePerUnit: item.pricePerUnit ?? null,
      netCost: item.netCost ?? null,
      markupFactor: item.markupFactor ?? null,
      budget: item.budget ?? null,
      grossCost: item.grossCost ?? null,
      rowIndex: item.rowIndex,
    }),
  );

  try {
    let updatedForm = formData.value;
    if (permissionTo.value?.edit.budget) {
      const editResponse = await calculationBudgetService.put(projectId, body);
      updatedForm = updatedForm.map((row) => {
        const updatedRow = editResponse.find(
          (item) => item.rowIndex === row.rowIndex,
        );
        return updatedRow ? { ...row, id: updatedRow.id ?? row.id } : row;
      });
    }

    if (permissionTo.value?.edit.comments) {
      const commentsResponse = await calculationBudgetService.updateComments(
        projectId,
        {
          comments: updatedForm.map((row) => ({
            id: row.id ?? '',
            comment: row.comment,
          })),
        },
      );
      updatedForm = updatedForm.map((row) => {
        const updatedComment = commentsResponse.find(
          (item) => item.id === row.id,
        );
        return updatedComment ? { ...row, ...updatedComment } : row;
      });
    }

    Object.assign(formData.value, updatedForm);
    notificationService.open({
      text: t('notifications.saveSuccess'),
      type: 'success',
    });
    originalFormData.value = utils.json.deepCopy(formData.value);
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const addCalculationBudgetRow = () => {
  const row: formDataInterface = {
    id: undefined,
    unit: units.Omg,
    netCost: null,
    budget: null,
    comment: null,
    markupFactor: null,
    numberOfItems: 1,
    accountNumber: 0,
    pricePerUnit: null,
    grossCost: null,
    rowIndex: formData.value.length,
  };

  formData.value.push(row);
};

const removeRow = (rowIndex: number) => {
  if (!formData.value) return;
  formData.value = formData.value.filter((row) => row.rowIndex !== rowIndex);
  formData.value.forEach((row, index) => {
    row.rowIndex = index;
  });
};

watch(
  () => formData.value,
  () => {
    formData.value.forEach((item) => {
      item.netCost = calculateNetCost(
        item.pricePerUnit ?? 0,
        item.numberOfItems ?? 0,
      );
      item.grossCost = calculateGrossCost(
        item.pricePerUnit ?? 0,
        item.numberOfItems ?? 0,
        item.markupFactor ?? 0,
      );
    });
  },
  { deep: true },
);

const validateFields = (): boolean => {
  let isValid = true;

  formData.value.forEach((row, index) => {
    if (!row.accountNumber || row.accountNumber == 0) {
      errors.value[`${row.id}-${index}`] = true;
      isValid = false;
    } else {
      errors.value[`${row.id}-${index}`] = false;
    }
  });

  return isValid;
};

onMounted(async () => {
  try {
    accounts.value = await fetchAccounts();
    const response = await calculationBudgetService.getAll(projectId.value);

    let newFormData = response;

    if (response && response.length === 0) {
      await calculationBudgetService.setupInitialRows(projectId.value);
      newFormData = await calculationBudgetService.getAll(projectId.value);
    }

    formData.value = newFormData.map((item) => ({
      id: item.id ?? undefined,
      unit: item.unit ?? null,
      netCost: item.netCost ?? null,
      budget: item.budget ?? null,
      comment: item.comment ?? null,
      markupFactor: item.markupFactor ?? null,
      numberOfItems: item.numberOfItems ?? null,
      accountNumber: item.accountNumber ?? null,
      pricePerUnit: item.pricePerUnit ?? null,
      grossCost: item.grossCost ?? null,
      rowIndex: item.rowIndex,
    }));

    originalFormData.value = utils.json.deepCopy(formData.value);
    originalFormData.value.forEach((item) => {
      item.netCost = calculateNetCost(
        item.pricePerUnit ?? 0,
        item.numberOfItems ?? 0,
      );
      item.grossCost = calculateGrossCost(
        item.pricePerUnit ?? 0,
        item.numberOfItems ?? 0,
        item.markupFactor ?? 0,
      );
    });
  } catch (error) {
    console.error(error);
  } finally {
    overlaySpinnerService.hide();
  }
});

const exportToCSV = async () => {
  overlaySpinnerService.show();
  try {
    const file = await calculationBudgetService.exportToCsv(
      utils.type.asType<string>(route.params.id),
    );
    if (file) {
      const blob = new Blob([file], { type: 'text/csv' });
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `kalkyl-och-budget-${currentDate}.csv`;
      utils.downloadFile(blob, fileName);
    } else {
      notificationService.open({
        text: t('notifications.exportToCsvError'),
        type: 'danger',
      });
    }
  } finally {
    overlaySpinnerService.hide();
  }
};

const moveRowUp = (displayIndex: number, displayData: formDataInterface[]) => {
  if (displayIndex === 0) return;
  const currentRow = displayData[displayIndex];
  const prevRow = displayData[displayIndex - 1];
  const currentIdx = formData.value.indexOf(currentRow);
  const prevIdx = formData.value.indexOf(prevRow);
  if (currentIdx < 0 || prevIdx < 0) return;
  formData.value[currentIdx] = prevRow;
  formData.value[prevIdx] = currentRow;
  currentRow.rowIndex = prevIdx;
  prevRow.rowIndex = currentIdx;
};

const moveRowDown = (
  displayIndex: number,
  displayData: formDataInterface[],
) => {
  if (displayIndex === displayData.length - 1) return;
  const currentRow = displayData[displayIndex];
  const nextRow = displayData[displayIndex + 1];
  const currentIdx = formData.value.indexOf(currentRow);
  const nextIdx = formData.value.indexOf(nextRow);
  if (currentIdx < 0 || nextIdx < 0) return;
  formData.value[currentIdx] = nextRow;
  formData.value[nextIdx] = currentRow;
  currentRow.rowIndex = nextIdx;
  nextRow.rowIndex = currentIdx;
};

const copyCalculationToBudget = () => {
  formData.value.forEach((row) => {
    if (row.budget !== row.netCost) {
      row.budget = row.netCost;
    }
  });
};

const permissionTo = computed(
  () => pagePermissions(projectId.value, user)?.calculateBudget,
);

const filteredPermissionColumns = computed(() => {
  let filteredColumns = columns;
  if (!permissionTo.value?.read.comments) {
    filteredColumns = columns.filter((column) => column.key !== 'comment');
  }
  if (!permissionTo.value?.edit.budget) {
    filteredColumns = filteredColumns.filter(
      (column) => column.key !== 'actions',
    );
  }
  return filteredColumns;
});

onBeforeRouteLeave((to, from, next) => {
  if (
    JSON.stringify(formData.value) !== JSON.stringify(originalFormData.value)
  ) {
    const confirmLeave = window.confirm(t('unsavedChangesNotification'));

    if (confirmLeave) {
      next();
    } else {
      next(false);
    }
  } else {
    next();
  }
});
</script>
<template>
  <div>
    <form @submit.prevent="saveCalculationBudgets">
      <SimpleTable
        :columns="filteredPermissionColumns"
        :data="formData"
        :sum-column-keys="['grossCost', 'netCost', 'budget']"
        :row-clickable="false"
        :enable-search="true"
        :input-columns="['numberOfItems', 'unit', 'markupFactor']"
        :has-edit-permission="permissionTo?.edit.budget"
        :dropdown-options="{ unit: units, accountNumber: accounts }"
        @header-input="handleHeaderInput"
      >
        <template #tbody="{ data }">
          <tr
            v-for="(row, index) in data"
            :key="row.id ?? `new-${row.rowIndex}`"
          >
            <td
              v-for="column in filteredPermissionColumns"
              :key="column.key"
              :class="[
                column.align || '',
                column.class ? column.class(row[column.key]) : '',
                column.key,
              ]"
              class="is-vcentered"
            >
              <template v-if="column.editable">
                <template v-if="column.key === 'accountNumber'">
                  <div v-if="permissionTo?.edit.budget">
                    <drop-down
                      v-model="row[column.key]"
                      :items="accounts ?? []"
                      :label="t('dropdownLabel')"
                    ></drop-down>
                    <p
                      v-if="errors[`${row.id}-${index}`]"
                      :id="`error-${row.id}-${index}`"
                      class="help is-danger"
                    >
                      {{ t('errorSelectAccount') }}
                    </p>
                  </div>
                  <div v-else>
                    {{
                      accounts?.find(
                        (account) =>
                          account.value === row[column.key].toString(),
                      )?.text
                    }}
                  </div>
                </template>
                <template v-else-if="column.key === 'unit'">
                  <div class="select unit" v-if="permissionTo?.edit.budget">
                    <select v-model="row[column.key]">
                      <option
                        v-for="(value, key) in units"
                        :key="key"
                        :value="value"
                      >
                        {{ value }}
                      </option>
                    </select>
                  </div>
                  <div v-else>
                    {{
                      column.format
                        ? column.format(row[column.key])
                        : row[column.key]
                    }}
                  </div>
                </template>
                <template v-else>
                  <div class="field">
                    <div
                      class="control"
                      :class="{
                        'has-icons-right':
                          column.prefix || column.key === 'pricePerUnit',
                      }"
                    >
                      <textarea
                        v-if="column.key === 'comment'"
                        class="textarea is-hovered"
                        v-model="row[column.key]"
                        rows="1"
                        :disabled="!permissionTo?.edit.comments"
                      ></textarea>
                      <div v-else-if="permissionTo?.edit.budget">
                        <formatted-number-input
                          v-model="row[column.key]"
                          step="0.01"
                          v-if="column.editableInputType === 'number'"
                        />
                        <input
                          class="input is-hovered"
                          v-else
                          v-model="row[column.key]"
                        />
                        <span
                          class="icon is-small is-right"
                          v-if="column.key === 'pricePerUnit'"
                        >
                          {{
                            t('calculationBudget.table.pricePerUnitCost', {
                              unit: row.unit,
                            })
                          }}
                        </span>
                        <span
                          class="icon is-small is-right"
                          v-if="column.prefix"
                        >
                          {{ column.prefix }}
                        </span>
                      </div>
                      <div v-else>
                        <div v-if="column.key === 'pricePerUnit'">
                          {{
                            column.format
                              ? column.format(row[column.key])
                              : row[column.key]
                          }}
                          {{
                            t('calculationBudget.table.pricePerUnitCost', {
                              unit: row.unit,
                            })
                          }}
                        </div>
                        <div v-else-if="column.key === 'markupFactor'">
                          {{
                            column.format
                              ? column.format(row[column.key])
                              : row[column.key]
                          }}%
                        </div>
                        <div v-else>
                          {{
                            column.format
                              ? column.format(row[column.key])
                              : row[column.key]
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <template v-else-if="column.key === 'actions'">
                <div class="is-flex">
                  <button
                    type="button"
                    class="button is-small mr-1"
                    :data-test="'move-up-button-' + row.id"
                    :disabled="index === 0 || !permissionTo?.edit.budget"
                    @click="moveRowUp(index, data)"
                  >
                    <PhArrowUp :size="18" />
                  </button>
                  <button
                    type="button"
                    class="button is-small mr-1"
                    :data-test="'move-down-button-' + row.id"
                    :disabled="
                      index === data.length - 1 || !permissionTo?.edit.budget
                    "
                    @click="moveRowDown(index, data)"
                  >
                    <PhArrowDown :size="18" />
                  </button>
                  <button
                    type="button"
                    class="button is-small is-danger"
                    :disabled="!permissionTo?.edit.budget"
                    @click="removeRow(row.rowIndex)"
                  >
                    <PhTrash :size="18" />
                  </button>
                </div>
              </template>
              <template v-else>
                {{
                  column.format
                    ? column.format(row[column.key])
                    : row[column.key]
                }}
              </template>
            </td>
          </tr>
          <tr v-if="permissionTo?.edit.budget">
            <td>
              <button
                class="button is-small is-light add-row"
                @click="addCalculationBudgetRow()"
                type="button"
              >
                <ph-plus size="14" weight="bold" class="mr-1"></ph-plus>
                {{ t('ata.details.addCostButton') }}
              </button>
            </td>
            <td
              v-for="item in filteredPermissionColumns.length - 1"
              :key="item"
            ></td>
          </tr>
        </template>
      </SimpleTable>
      <div>
        <button
          class="button is-text is-float-left"
          @click="exportToCSV()"
          data-test="export-to-csv"
          type="button"
        >
          <PhFileArrowDown class="mr-1" :size="22" />
          {{ t('exportToCsvButton') }}
        </button>
        <button
          class="button save is-float-right"
          :class="{ 'is-loading': loading }"
          type="submit"
          v-if="permissionTo?.edit.budget || permissionTo?.edit.comments"
        >
          <PhFloppyDisk :size="18" class="mr-2" />
          {{ t('invoices.splitInvoice.save') }}
        </button>
        <button
          class="button is-float-right mr-2"
          type="button"
          data-test="copy-calculation-to-budget"
          @click="copyCalculationToBudget()"
          v-if="permissionTo?.edit.budget"
        >
          {{ t('calculationBudget.copyCalculationToBudgetBtn') }}
        </button>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
tr {
  background-color: var(--table-light);
}

.pricePerUnit .control.has-icons-right .icon {
  width: 4.5rem;
}

.textarea {
  font-size: smaller;
  caret-color: auto;
  background-color: inherit;
  color: inherit;
  cursor: text;
}
</style>
